import React, { useEffect, useState, useCallback } from 'react';
import { evaluationPercentage } from '../../lib/evaluationIntegrity';
import { useNavigate } from 'react-router-dom';
import { locationsAPI } from '../../api/client';
import { VENUE_TYPES, STATUS_CONFIG } from '../../lib/locationConstants';
import { useAuth } from '../../context/AuthContext';
import SemaforoBadge from '../../components/SemaforoBadge';
import {
  Plus,
  MapPin,
  Search,
  LayoutGrid,
  List,
  X,
  Users,
  DoorOpen,
  Building2,
} from 'lucide-react';
import { toast } from 'sonner';
import ArchiveButton from '../../components/ArchiveButton';

function StatCard({ label, value, color }) {
  return (
    <div className="soem-card p-5 border-t-4" style={{ borderTopColor: color }}>
      <p className="soem-label mb-1">{label}</p>
      <p className="text-3xl font-black text-soem-navy">{value}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.inactiva;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cfg.bg}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function LocationCard({ location, onView, onEdit, onStatusChange, canEdit }) {
  const vt = VENUE_TYPES[location.venue_type] || { label: location.venue_type, emoji: '🏢' };
  const addr = location.address || {};
  const last = location.last_evaluation;

  return (
    <div className="soem-card hover:shadow-md transition-shadow overflow-hidden" data-testid={`loc-card-${location.id}`}>
      {/* Photo or placeholder */}
      <div className="h-40 bg-slate-100 flex items-center justify-center relative overflow-hidden">
        {location.photos?.length > 0 ? (
          <img
            src={`${process.env.REACT_APP_API_URL}${location.photos[0]}`}
            alt={location.name}
            className="w-full h-full object-cover"
            onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="flex flex-col items-center gap-1 text-slate-300">
            <Building2 size={40} />
            <span className="text-xs">{vt.emoji} {vt.label}</span>
          </div>
        )}
        <div className="absolute top-2 left-2">
          <StatusBadge status={location.status} />
        </div>
        <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded font-mono">
          {location.location_code}
        </div>
      </div>

      <div className="p-4 space-y-2">
        <div>
          <h3 className="font-bold text-soem-navy text-base leading-tight">{location.name}</h3>
          <span className="inline-flex items-center gap-1 text-xs text-slate-500 mt-0.5">
            <span>{vt.emoji}</span> {vt.label}
          </span>
        </div>

        <p className="text-xs text-slate-500 flex items-center gap-1">
          <MapPin size={11} />
          {[addr.city, addr.colony].filter(Boolean).join(', ') || addr.city || '—'}
        </p>

        <div className="flex items-center gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1">
            <Users size={11} />
            {(location.max_capacity || 0).toLocaleString()} personas
          </span>
          <span className="flex items-center gap-1">
            <DoorOpen size={11} />
            {(location.access_points?.entrances || 0)}E / {(location.access_points?.exits || 0)}S
          </span>
        </div>

        {/* Last evaluation */}
        <div className="border-t border-slate-100 pt-2">
          {last ? (
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">Última eval.</span>
              <SemaforoBadge semaforo={last.semaforo} percentage={evaluationPercentage(last)} size="sm" />
            </div>
          ) : (
            <p className="text-xs text-slate-300 italic">Sin evaluaciones</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-1">
          <button onClick={() => onView(location.id)}
            className="soem-btn-primary flex-1 py-1.5 text-xs" data-testid={`view-${location.id}`}>
            Ver detalle
          </button>
          {canEdit && (
            <button onClick={() => onEdit(location.id)}
              className="soem-btn-secondary px-3 py-1.5 text-xs">
              Editar
            </button>
          )}
          {canEdit && (
            <select
              className="text-xs border border-slate-300 rounded-sm px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-soem-primary bg-white"
              value={location.status}
              aria-label="Cambiar estado"
              onChange={e => onStatusChange(location.id, e.target.value)}
              data-testid={`status-${location.id}`}
            >
              <option value="activa">Activa</option>
              <option value="en_mantenimiento">Mantenimiento</option>
              <option value="inactiva">Inactiva</option>
            </select>
          )}
        </div>
      </div>
    </div>
  );
}

export default function LocationsList() {
  const navigate = useNavigate();
  const { isAdmin, isLider } = useAuth();
  const canEdit = isAdmin || isLider;

  const [locations, setLocations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => localStorage.getItem('loc_view') || 'cards');
  const [filters, setFilters] = useState({ q: '', venue_type: '', status: '' });
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.q) params.search = filters.q;
      if (filters.venue_type) params.venue_type = filters.venue_type;
      if (filters.status) params.status = filters.status;

      // Stats is lider+ only; Levantador needs the list page to render
      // even when stats returns 403. Don't let it block the locations grid.
      const [locsRes, statsRes] = await Promise.all([
        locationsAPI.list(params),
        locationsAPI.stats().catch(() => ({ data: null })),
      ]);
      setLocations((locsRes.data.items || []).filter(i => !i.is_archived));
      setTotal(locsRes.data.total || 0);
      setStats(statsRes.data);
    } catch { toast.error('Error al cargar locaciones'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const changeView = (v) => { setView(v); localStorage.setItem('loc_view', v); };

  const handleStatusChange = async (id, status) => {
    try {
      await locationsAPI.changeStatus(id, status);
      toast.success('Status actualizado');
      load();
    } catch { toast.error('Error al cambiar status'); }
  };

  const clearFilters = () => setFilters({ q: '', venue_type: '', status: '' });
  const hasFilters = filters.q || filters.venue_type || filters.status;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="locations-list">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-soem-navy">Directorio de Locaciones</h1>
          <p className="text-slate-500 text-sm mt-1">{total} locaciones registradas</p>
        </div>
        {canEdit && (
          <button onClick={() => navigate('/dashboard/locations/new')}
            className="soem-btn-primary px-4 py-2 text-sm flex items-center gap-2"
            data-testid="new-location-btn">
            <Plus size={16} /> Nueva Locación
          </button>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Locaciones" value={stats.total_locations} color="#1A56DB" />
          <StatCard label="Activas" value={stats.by_status?.activa || 0} color="#15803D" />
          <StatCard label="En Mantenimiento" value={stats.by_status?.en_mantenimiento || 0} color="#D97706" />
          <StatCard label="Inactivas" value={stats.by_status?.inactiva || 0} color="#94a3b8" />
        </div>
      )}

      {/* Filters */}
      <div className="soem-card p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-48">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="pl-9 pr-4 py-2 border border-slate-300 rounded-sm text-sm w-full focus:outline-none focus:ring-2 focus:ring-soem-primary"
              placeholder="Buscar por nombre, ciudad..." aria-label="Buscar locación"
              value={filters.q}
              onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
              data-testid="loc-search"
            />
          </div>

          <select className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
            value={filters.venue_type} aria-label="Filtrar por tipo de recinto" onChange={e => setFilters(f => ({ ...f, venue_type: e.target.value }))}
            data-testid="venue-type-filter">
            <option value="">Todos los tipos</option>
            {Object.entries(VENUE_TYPES).map(([k, v]) => (
              <option key={k} value={k}>{v.emoji} {v.label}</option>
            ))}
          </select>

          <select className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
            value={filters.status} aria-label="Filtrar por estado" onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
            data-testid="status-filter">
            <option value="">Todos los estados</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>

          {hasFilters && (
            <button aria-label="Cerrar" onClick={clearFilters}
              className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-soem-primary transition-colors"
              data-testid="clear-filters-btn">
              <X size={14} /> Limpiar
            </button>
          )}

          <div className="flex items-center gap-1 ml-auto border border-slate-300 rounded-sm overflow-hidden">
            <button onClick={() => changeView('cards')}
              className={`p-2 ${view === 'cards' ? 'bg-soem-primary text-white' : 'text-slate-400 hover:bg-slate-50'}`}
              data-testid="view-cards-btn">
              <LayoutGrid size={15} />
            </button>
            <button onClick={() => changeView('table')}
              className={`p-2 ${view === 'table' ? 'bg-soem-primary text-white' : 'text-slate-400 hover:bg-slate-50'}`}
              data-testid="view-table-btn">
              <List size={15} />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className={view === 'cards' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5' : ''}>
          {[...Array(4)].map((_, i) => (
            <div key={i} className={`soem-card animate-pulse bg-slate-100 ${view === 'cards' ? 'h-72' : 'h-14 mb-2'}`} />
          ))}
        </div>
      ) : locations.length === 0 ? (
        <div className="soem-card p-16 text-center">
          <MapPin size={48} className="text-slate-200 mx-auto mb-4" />
          <p className="text-slate-500 font-medium">
            {hasFilters ? 'No se encontraron locaciones con esos filtros.' : 'No hay locaciones registradas.'}
          </p>
          {canEdit && !hasFilters && (
            <button onClick={() => navigate('/dashboard/locations/new')}
              className="soem-btn-primary px-5 py-2 text-sm mt-4 inline-flex items-center gap-2">
              <Plus size={14} /> Crear primera locación
            </button>
          )}
        </div>
      ) : view === 'cards' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {locations.map(loc => (
            <LocationCard key={loc.id} location={loc} canEdit={canEdit}
              onView={id => navigate(`/dashboard/locations/${id}`)}
              onEdit={id => navigate(`/dashboard/locations/${id}/edit`)}
              onStatusChange={handleStatusChange} />
          ))}
        </div>
      ) : (
        <div className="soem-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="soem-table-header">
                <th className="px-4 py-3 text-left">Código</th>
                <th className="px-4 py-3 text-left">Nombre</th>
                <th className="px-4 py-3 text-left">Tipo</th>
                <th className="px-4 py-3 text-left">Ciudad</th>
                <th className="px-4 py-3 text-left">Aforo Permitido</th>
                <th className="px-4 py-3 text-left">Accesos</th>
                <th className="px-4 py-3 text-left">Últ. Evaluación</th>
                <th className="px-4 py-3 text-left">Estatus</th>
                <th className="px-4 py-3 text-left">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {locations.map(loc => {
                const vt = VENUE_TYPES[loc.venue_type] || { label: loc.venue_type, emoji: '🏢' };
                const last = loc.last_evaluation;
                return (
                  <tr key={loc.id} className="soem-table-row" data-testid={`loc-row-${loc.id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-400">{loc.location_code}</td>
                    <td className="px-4 py-3 font-semibold text-soem-navy">{loc.name}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{vt.emoji} {vt.label}</td>
                    <td className="px-4 py-3 text-slate-500">{loc.address?.city || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{(loc.max_capacity || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {loc.access_points?.entrances || 0}E / {loc.access_points?.exits || 0}S
                    </td>
                    <td className="px-4 py-3">
                      {last ? (
                        <SemaforoBadge semaforo={last.semaforo} percentage={evaluationPercentage(last)} size="sm" />
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={loc.status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => navigate(`/dashboard/locations/${loc.id}`)}
                          className="text-soem-primary text-xs hover:underline">Ver</button>
                        {canEdit && (
                          <button onClick={() => navigate(`/dashboard/locations/${loc.id}/edit`)}
                            className="text-slate-400 text-xs hover:text-soem-primary">Editar</button>
                        )}
                        {isAdmin && <ArchiveButton entity="locations" id={loc.id} name={loc.name} onArchived={load} />}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
